import { useGetPatientResponsibilityStatus } from "@/api/generated";
import {
  CheckPatientResponsibilityWorkflowStatusStatus,
  PolicyStatus,
} from "@/api/generated/models";
import { useGetUserProfile } from "@/api/queries/userProfile";

// The backend pushes a `patient_responsibility_updated` socket event when a check finishes, which is
// what ideally ends the wait. This is a safety net for a socket event that never lands.
const IN_PROGRESS_POLL_INTERVAL_MS = 5000;

export const DISABLED_REASONS = {
  noInsurance: "Add insurance in Settings to see copay pricing",
  workflowFailed: "We couldn't verify your insurance",
  ineligible: "Your plan isn't accepted currently",
  invalid: "We couldn't verify your insurance",
  pending: "Verifying your insurance timed out",
  unknownCopay: "We couldn't confirm your copay",
} as const;

export type PatientResponsibilityState = {
  /** True when the Insurance segment can be selected. */
  isInsuranceEnabled: boolean;
  /** Reason to display next to the segmented control when Insurance is disabled. */
  disabledReason: string | null;
  /** True while the eligibility workflow is polling and no result is available yet. */
  isLoading: boolean;
  /** Patient responsibility amount in cents to display in Insurance mode. */
  patientResponsibilityCents: number | null;
  /** Raw Bridge policy status for diagnostics/tracking. */
  policyStatus: PolicyStatus | null;
  /**
   * Vendor-neutral id of the eligibility check run the copay was quoted from. Forwarded into the
   * progression at visit-option selection so checkout charges the exact amount shown here. Only
   * set when Insurance is enabled with a confirmed copay; null otherwise.
   */
  runId: string | null;
};

export const usePatientResponsibility = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): PatientResponsibilityState => {
  const { data: userProfile } = useGetUserProfile();
  const insuranceOnFile = Boolean(userProfile?.bridgePayerId);

  const { data: statusData, isError: statusIsError } =
    useGetPatientResponsibilityStatus(undefined, {
      query: {
        enabled: enabled && insuranceOnFile,
        meta: { suppressErrorToast: true },
        refetchInterval: (query) =>
          query.state.data?.status ===
          CheckPatientResponsibilityWorkflowStatusStatus.in_progress
            ? IN_PROGRESS_POLL_INTERVAL_MS
            : false,
      },
    });

  // Return a neutral state; the caller already hides the toggle when disabled
  if (!enabled) {
    return {
      isInsuranceEnabled: false,
      disabledReason: null,
      isLoading: false,
      patientResponsibilityCents: null,
      runId: null,
      policyStatus: null,
    };
  }

  if (!insuranceOnFile) {
    return {
      isInsuranceEnabled: false,
      disabledReason: DISABLED_REASONS.noInsurance,
      isLoading: false,
      patientResponsibilityCents: null,
      runId: null,
      policyStatus: null,
    };
  }

  if (statusIsError) {
    return {
      isInsuranceEnabled: false,
      disabledReason: DISABLED_REASONS.workflowFailed,
      isLoading: false,
      patientResponsibilityCents: null,
      runId: null,
      policyStatus: null,
    };
  }

  const status = statusData?.status;
  const result = statusData?.result ?? null;
  const policyStatus = result?.policy_status ?? null;

  if (status === CheckPatientResponsibilityWorkflowStatusStatus.failed) {
    return {
      isInsuranceEnabled: false,
      disabledReason: DISABLED_REASONS.workflowFailed,
      isLoading: false,
      patientResponsibilityCents: null,
      runId: null,
      policyStatus,
    };
  }

  if (status === CheckPatientResponsibilityWorkflowStatusStatus.completed) {
    if (policyStatus === PolicyStatus.PENDING) {
      return {
        isInsuranceEnabled: false,
        disabledReason: DISABLED_REASONS.pending,
        isLoading: false,
        patientResponsibilityCents: null,
        runId: null,
        policyStatus,
      };
    }
    if (policyStatus === PolicyStatus.INVALID) {
      return {
        isInsuranceEnabled: false,
        disabledReason: DISABLED_REASONS.invalid,
        isLoading: false,
        patientResponsibilityCents: null,
        runId: null,
        policyStatus,
      };
    }

    const patientResponsibilityCents = result?.patient_responsibility ?? null;
    const eligibleWithCopay =
      result?.eligible === true &&
      policyStatus === PolicyStatus.CONFIRMED &&
      patientResponsibilityCents !== null;

    if (!eligibleWithCopay) {
      // eligible=true but patient responsibility is null (rare) → treat as "can't confirm"; otherwise ineligible.
      const reason =
        result?.eligible && patientResponsibilityCents === null
          ? DISABLED_REASONS.unknownCopay
          : DISABLED_REASONS.ineligible;
      return {
        isInsuranceEnabled: false,
        disabledReason: reason,
        isLoading: false,
        patientResponsibilityCents: null,
        runId: null,
        policyStatus,
      };
    }

    return {
      isInsuranceEnabled: true,
      disabledReason: null,
      isLoading: false,
      patientResponsibilityCents,
      policyStatus,
      runId: result?.run_id ?? null,
    };
  }

  // in_progress or no data yet — allow the user to select Insurance; card will show a spinner.
  return {
    isInsuranceEnabled: true,
    disabledReason: null,
    isLoading: true,
    patientResponsibilityCents: null,
    policyStatus,
    runId: null,
  };
};
